import { useState, useEffect, useRef, useCallback } from 'react'
import {
  PaperAirplaneIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline'
import { Avatar, Badge, InlineSpinner } from '@/components/ui'
import { taskforceApi } from '@/api'
import type { ChatMessage, OnlineMember } from '@/api/taskforce'
import { useAuthStore } from '@/store/authStore'
import toast from 'react-hot-toast'

interface TaskForceChatProps {
  taskforceSlug: string
  isMember: boolean
}

export default function TaskForceChat({ taskforceSlug, isMember }: TaskForceChatProps) {
  const { user } = useAuthStore()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [newMessage, setNewMessage] = useState('')
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null)
  const [onlineMembers, setOnlineMembers] = useState<OnlineMember[]>([])
  const [wsConnected, setWsConnected] = useState(false)
  const [typingUsers, setTypingUsers] = useState<Map<number, string>>(new Map())

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const wsRef = useRef<WebSocket | null>(null)
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Load initial messages (only if member)
  useEffect(() => {
    if (!isMember) return
    loadMessages()
    loadOnlineMembers()
  }, [taskforceSlug, isMember])

  // Setup WebSocket connection
  useEffect(() => {
    if (!isMember) return

    const token = localStorage.getItem('access_token')
    if (!token) return

    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsHost = window.location.hostname
    const wsPort = window.location.port ? `:${window.location.port}` : ''
    const wsUrl = `${wsProtocol}//${wsHost}${wsPort}/ws/taskforce/${taskforceSlug}/chat/?token=${token}`

    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      console.log('WebSocket connected')
      setWsConnected(true)
    }

    ws.onclose = (event) => {
      console.log('WebSocket disconnected', event.code)
      setWsConnected(false)
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
    }

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data)
      handleWsMessage(data)
    }

    return () => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.close()
      }
    }
  }, [taskforceSlug, isMember])

  const handleWsMessage = (data: any) => {
    switch (data.type) {
      case 'message':
        setMessages(prev => [...prev, {
          id: data.message.id,
          taskforce: 0,
          sender: data.message.sender_id,
          sender_name: data.message.sender_name,
          sender_avatar: data.message.sender_avatar,
          content: data.message.content,
          message_type: data.message.message_type || 'text',
          attachment: null,
          reply_to: data.message.reply_to,
          reply_to_preview: null,
          is_edited: data.message.is_edited || false,
          edited_at: null,
          is_deleted: false,
          created_at: data.message.created_at,
        }])
        scrollToBottom()
        break

      case 'typing':
        if (data.is_typing) {
          setTypingUsers(prev => new Map(prev).set(data.user_id, data.username))
        } else {
          setTypingUsers(prev => {
            const next = new Map(prev)
            next.delete(data.user_id)
            return next
          })
        }
        break

      case 'user_status':
        if (data.status === 'online') {
          setOnlineMembers(prev => {
            if (!prev.find(m => m.id === data.user_id)) {
              return [...prev, { id: data.user_id, name: data.username }]
            }
            return prev
          })
        } else {
          setOnlineMembers(prev => prev.filter(m => m.id !== data.user_id))
        }
        break

      case 'message_edited':
        setMessages(prev => prev.map(msg =>
          msg.id === data.message_id
            ? { ...msg, content: data.content, is_edited: true, edited_at: data.edited_at }
            : msg
        ))
        break

      case 'message_deleted':
        setMessages(prev => prev.map(msg =>
          msg.id === data.message_id
            ? { ...msg, is_deleted: true, content: '[Messaggio eliminato]' }
            : msg
        ))
        break
    }
  }

  const loadMessages = async () => {
    setLoading(true)
    try {
      const data = await taskforceApi.getChatMessages(taskforceSlug, { limit: 50 })
      setMessages(data.messages)
      setHasMore(data.has_more)
      scrollToBottom()
    } catch (error) {
      console.error('Error loading messages:', error)
      toast.error('Errore nel caricamento dei messaggi')
    } finally {
      setLoading(false)
    }
  }

  const loadMoreMessages = async () => {
    if (loadingMore || !hasMore || messages.length === 0) return

    setLoadingMore(true)
    try {
      const oldestMessage = messages[0]
      const data = await taskforceApi.getChatMessages(taskforceSlug, {
        limit: 50,
        before: oldestMessage.id
      })
      setMessages(prev => [...data.messages, ...prev])
      setHasMore(data.has_more)
    } catch (error) {
      console.error('Error loading more messages:', error)
    } finally {
      setLoadingMore(false)
    }
  }

  const loadOnlineMembers = async () => {
    try {
      const data = await taskforceApi.getOnlineMembers(taskforceSlug)
      setOnlineMembers(data.online_members)
    } catch (error) {
      console.error('Error loading online members:', error)
    }
  }

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }, [])

  const handleScroll = () => {
    const container = messagesContainerRef.current
    if (container && container.scrollTop === 0 && hasMore && !loadingMore) {
      loadMoreMessages()
    }
  }

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return

    const content = newMessage.trim()
    setNewMessage('')
    setSending(true)

    // Try WebSocket first
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'message',
        content,
        reply_to: replyTo?.id
      }))
      setSending(false)
      setReplyTo(null)
    } else {
      // Fallback to REST API
      try {
        await taskforceApi.sendChatMessage(taskforceSlug, {
          content,
          reply_to: replyTo?.id
        })
        setReplyTo(null)
        // Message will be added via REST response or we need to refresh
        loadMessages()
      } catch (error) {
        console.error('Error sending message:', error)
        toast.error('Errore nell\'invio del messaggio')
        setNewMessage(content) // Restore message
      } finally {
        setSending(false)
      }
    }
  }

  const sendTypingIndicator = (isTyping: boolean) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'typing',
        is_typing: isTyping
      }))
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value)

    // Send typing indicator
    sendTypingIndicator(true)

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current)
    }

    // Set timeout to stop typing indicator
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(false)
    }, 2000)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return 'Oggi'
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Ieri'
    } else {
      return date.toLocaleDateString('it-IT', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
      })
    }
  }

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = new Date(message.created_at).toDateString()
    if (!groups[date]) {
      groups[date] = []
    }
    groups[date].push(message)
    return groups
  }, {} as Record<string, ChatMessage[]>)

  if (!isMember) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <ChatBubbleLeftRightIcon className="h-12 w-12 mb-4" />
        <p>Unisciti alla Task Force per accedere alla chat</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[500px] bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-600" />
          <h3 className="font-medium text-gray-900">Chat Team</h3>
          {wsConnected ? (
            <Badge variant="success" size="sm">Online</Badge>
          ) : (
            <Badge variant="default" size="sm">Offline</Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <UserCircleIcon className="h-4 w-4" />
          <span>{onlineMembers.length} online</span>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {loading ? (
          <div className="flex justify-center">
            <InlineSpinner message="Caricamento messaggi..." />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <ChatBubbleLeftRightIcon className="h-12 w-12 mb-2" />
            <p>Nessun messaggio ancora</p>
            <p className="text-sm">Inizia la conversazione!</p>
          </div>
        ) : (
          <>
            {loadingMore && (
              <div className="flex justify-center py-2">
                <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            )}
            {hasMore && !loadingMore && (
              <button
                onClick={loadMoreMessages}
                className="w-full text-center text-sm text-primary-600 hover:underline py-2"
              >
                Carica messaggi precedenti
              </button>
            )}

            {Object.entries(groupedMessages).map(([date, dateMessages]) => (
              <div key={date}>
                <div className="flex items-center justify-center my-4">
                  <div className="bg-gray-100 text-gray-500 text-xs px-3 py-1 rounded-full">
                    {formatDate(dateMessages[0].created_at)}
                  </div>
                </div>

                {dateMessages.map((message, index) => {
                  const isOwn = message.sender === user?.id
                  const showAvatar = index === 0 || dateMessages[index - 1].sender !== message.sender

                  return (
                    <div
                      key={message.id}
                      className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : ''} ${
                        showAvatar ? 'mt-4' : 'mt-1'
                      }`}
                    >
                      {showAvatar ? (
                        <Avatar
                          size="sm"
                          name={message.sender_name}
                          src={message.sender_avatar || undefined}
                        />
                      ) : (
                        <div className="w-8" />
                      )}

                      <div className={`max-w-[70%] ${isOwn ? 'items-end' : 'items-start'}`}>
                        {showAvatar && !isOwn && (
                          <p className="text-xs text-gray-500 mb-1 ml-1">{message.sender_name}</p>
                        )}

                        {message.reply_to_preview && (
                          <div className="text-xs bg-gray-100 rounded px-2 py-1 mb-1 border-l-2 border-primary-400">
                            <span className="text-primary-600 font-medium">
                              {message.reply_to_preview.sender_name}
                            </span>
                            <p className="text-gray-600 truncate">{message.reply_to_preview.content}</p>
                          </div>
                        )}

                        <div
                          className={`rounded-2xl px-4 py-2 ${
                            isOwn
                              ? 'bg-primary-600 text-white rounded-br-md'
                              : 'bg-gray-100 text-gray-900 rounded-bl-md'
                          } ${message.is_deleted ? 'italic opacity-60' : ''}`}
                        >
                          <p className="whitespace-pre-wrap break-words">{message.content}</p>
                        </div>

                        <div className={`flex items-center gap-1 mt-1 text-xs text-gray-400 ${
                          isOwn ? 'justify-end' : 'justify-start'
                        }`}>
                          <span>{formatTime(message.created_at)}</span>
                          {message.is_edited && <span>(modificato)</span>}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {typingUsers.size > 0 && (
        <div className="px-4 py-1 text-xs text-gray-500">
          {Array.from(typingUsers.values()).join(', ')} sta scrivendo...
        </div>
      )}

      {/* Reply preview */}
      {replyTo && (
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm">
            <span className="text-gray-500">Rispondi a </span>
            <span className="font-medium text-primary-600">{replyTo.sender_name}</span>
            <p className="text-gray-600 truncate text-xs">{replyTo.content}</p>
          </div>
          <button onClick={() => setReplyTo(null)} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Input */}
      <div className="px-4 py-3 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newMessage}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="Scrivi un messaggio..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={sending}
          />
          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            className="p-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {sending ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
            ) : (
              <PaperAirplaneIcon className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
